// Mock data generator for real-time simulation

export interface SiteData {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  tanks: TankData[];
  generators: GeneratorData[];
  alerts: AlertData[];
}

export interface TankData {
  id: string;
  name: string;
  capacity: number;
  currentLevel: number;
  currentVolume: number;
  fuelType: 'diesel' | 'petrol';
  lastRefill: string;
  quality: {
    status: 'good' | 'warning' | 'contaminated';
    waterContent: number;
    sediment: 'normal' | 'high';
    temperature: number;
  };
  sensors: {
    levelSensor: boolean;
    qualitySensor: boolean;
    flowMeter: boolean;
  };
}

export interface GeneratorData {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'maintenance';
  fuelConsumption: number; // L/hour
  runtime: number; // hours
  efficiency: number; // percentage
  lastMaintenance: string;
}

export interface AlertData {
  id: string;
  type: 'theft' | 'leak' | 'low_fuel' | 'contamination' | 'maintenance' | 'refill';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  timestamp: string;
  siteId: string;
  tankId?: string;
  generatorId?: string;
  acknowledged: boolean;
  resolved: boolean;
}

export interface ConsumptionData {
  timestamp: string;
  date: string;
  consumption: number;
  target: number;
  efficiency: number;
  prediction: number;
  generator1?: number;
  generator2?: number;
  generator3?: number;
}

export interface FuelLevelHistory {
  timestamp: string;
  level: number;
  volume: number;
  capacity: number;
  events?: Array<{
    type: 'refill' | 'theft' | 'leak' | 'maintenance';
    time: string;
    value: number;
  }>;
}

export interface TripRecord {
  id: string;
  date: string;
  fromSite: string;
  toSite: string;
  volumeDelivered: number;
  fuelType: 'petrol' | 'diesel';
  status: 'completed' | 'in-progress' | 'cancelled';
}

export interface TankerData {
  id: string;
  registrationNumber: string;
  fleetNumber: string;
  capacity: number;
  fuelType: 'petrol' | 'diesel';
  currentLoad: number;
  currentVolume: number;
  status: 'en-route' | 'idle' | 'loading' | 'delivering' | 'maintenance';
  location: { lat: number; lng: number; address: string };
  assignedDriverId: string;
  assignedSiteId: string;
  lastActivity: string;
}

export interface DriverData {
  id: string;
  name: string;
  phone: string;
  email: string;
  avatarInitials: string;
  licenseNumber: string;
  licenseExpiry: string;
  assignedTankerId: string;
  assignedSiteId: string;
  status: 'on-duty' | 'off-duty' | 'on-leave';
  totalTrips: number;
  totalVolume: number;
  trips: TripRecord[];
}

// Generate mock sites
export const generateMockSites = (): SiteData[] => {
  const sites: SiteData[] = [
    {
      id: 'site-a',
      name: 'Site A - Head Office',
      location: {
        lat: 6.5244,
        lng: 3.3792,
        address: 'Victoria Island, Lagos',
      },
      tanks: [],
      generators: [],
      alerts: [],
    },
    {
      id: 'site-b',
      name: 'Site B - Backup Facility',
      location: {
        lat: 6.4474,
        lng: 3.3903,
        address: 'Ikoyi, Lagos',
      },
      tanks: [],
      generators: [],
      alerts: [],
    },
    {
      id: 'site-c',
      name: 'Site C - Remote Station',
      location: {
        lat: 6.6018,
        lng: 3.3515,
        address: 'Surulere, Lagos',
      },
      tanks: [],
      generators: [],
      alerts: [],
    },
    {
      id: 'site-d',
      name: 'Site D - Storage Center',
      location: {
        lat: 6.5795,
        lng: 3.3211,
        address: 'Ikeja, Lagos',
      },
      tanks: [],
      generators: [],
      alerts: [],
    },
  ];

  // Add tanks to each site
  sites.forEach((site, siteIndex) => {
    const tankCount = Math.floor(Math.random() * 3) + 1; // 1-3 tanks per site

    for (let i = 0; i < tankCount; i++) {
      const capacity = [2000, 3000, 4000, 5000][Math.floor(Math.random() * 4)];
      const level = Math.random() * 100;

      site.tanks.push({
        id: `${site.id}-tank-${i + 1}`,
        name: `Tank ${i + 1}`,
        capacity,
        currentLevel: level,
        currentVolume: (level / 100) * capacity,
        fuelType: Math.random() > 0.8 ? 'petrol' : 'diesel',
        lastRefill: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        quality: {
          status: level < 15 ? 'warning' : Math.random() > 0.9 ? 'contaminated' : 'good',
          waterContent: Math.random() * 0.5,
          sediment: Math.random() > 0.95 ? 'high' : 'normal',
          temperature: 20 + Math.random() * 15,
        },
        sensors: {
          levelSensor: Math.random() > 0.1,
          qualitySensor: Math.random() > 0.2,
          flowMeter: Math.random() > 0.15,
        },
      });
    }

    // Add generators
    const generatorCount = Math.floor(Math.random() * 4) + 2; // 2-5 generators per site

    for (let i = 0; i < generatorCount; i++) {
      site.generators.push({
        id: `${site.id}-gen-${i + 1}`,
        name: `Generator ${i + 1}`,
        status: Math.random() > 0.3 ? 'running' : Math.random() > 0.8 ? 'maintenance' : 'stopped',
        fuelConsumption: 30 + Math.random() * 50,
        runtime: Math.random() * 24,
        efficiency: 70 + Math.random() * 25,
        lastMaintenance: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      });
    }
  });

  return sites;
};

// Generate alerts based on site data
export const generateAlertsForSites = (sites: SiteData[]): AlertData[] => {
  const alerts: AlertData[] = [];
  const alertTypes = ['theft', 'leak', 'low_fuel', 'contamination', 'maintenance', 'refill'] as const;

  sites.forEach((site) => {
    site.tanks.forEach((tank) => {
      // Low fuel alerts
      if (tank.currentLevel < 25) {
        alerts.push({
          id: `alert-${Date.now()}-${Math.random()}`,
          type: 'low_fuel',
          severity: tank.currentLevel < 10 ? 'critical' : 'medium',
          title: 'Low Fuel Level',
          message: `${site.name} - ${tank.name} is at ${tank.currentLevel.toFixed(1)}%`,
          timestamp: new Date().toISOString(),
          siteId: site.id,
          tankId: tank.id,
          acknowledged: Math.random() > 0.7,
          resolved: false,
        });
      }

      // Contamination alerts
      if (tank.quality.status === 'contaminated') {
        alerts.push({
          id: `alert-${Date.now()}-${Math.random()}`,
          type: 'contamination',
          severity: 'high',
          title: 'Fuel Contamination Detected',
          message: `${site.name} - ${tank.name} shows signs of contamination`,
          timestamp: new Date().toISOString(),
          siteId: site.id,
          tankId: tank.id,
          acknowledged: false,
          resolved: false,
        });
      }

      // Random theft/leak alerts
      if (Math.random() > 0.9) {
        alerts.push({
          id: `alert-${Date.now()}-${Math.random()}`,
          type: Math.random() > 0.5 ? 'theft' : 'leak',
          severity: 'critical',
          title: Math.random() > 0.5 ? 'Possible Fuel Theft' : 'Fuel Leak Detected',
          message: `${site.name} - Unusual fuel level change detected in ${tank.name}`,
          timestamp: new Date(Date.now() - Math.random() * 2 * 60 * 60 * 1000).toISOString(),
          siteId: site.id,
          tankId: tank.id,
          acknowledged: Math.random() > 0.5,
          resolved: false,
        });
      }
    });

    // Generator maintenance alerts
    site.generators.forEach((generator) => {
      if (generator.status === 'maintenance' || Math.random() > 0.95) {
        alerts.push({
          id: `alert-${Date.now()}-${Math.random()}`,
          type: 'maintenance',
          severity: 'low',
          title: 'Maintenance Required',
          message: `${site.name} - ${generator.name} requires maintenance`,
          timestamp: new Date().toISOString(),
          siteId: site.id,
          generatorId: generator.id,
          acknowledged: Math.random() > 0.6,
          resolved: false,
        });
      }
    });
  });

  return alerts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

// Generate historical consumption data
export const generateConsumptionData = (hours: number = 24): ConsumptionData[] => {
  const data: ConsumptionData[] = [];
  const baseConsumption = 2000;
  const target = 2400;

  for (let i = hours; i >= 0; i--) {
    const timestamp = new Date(Date.now() - i * 60 * 60 * 1000);
    const hour = timestamp.getHours();

    // Simulate daily consumption patterns
    let consumption = baseConsumption;
    if (hour >= 6 && hour <= 22) {
      consumption += Math.sin((hour - 6) / 16 * Math.PI) * 800; // Peak during day
    }

    // Add some randomness
    consumption += (Math.random() - 0.5) * 400;
    consumption = Math.max(0, consumption);

    // Calculate efficiency
    const efficiency = Math.min(100, (target / consumption) * 100);

    // Generate prediction (slight upward trend)
    const prediction = consumption * (1 + (Math.random() - 0.4) * 0.1);

    data.push({
      timestamp: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      date: timestamp.toISOString(),
      consumption: Math.round(consumption),
      target,
      efficiency: Math.round(efficiency),
      prediction: Math.round(prediction),
      generator1: Math.round(consumption * 0.4 + (Math.random() - 0.5) * 100),
      generator2: Math.round(consumption * 0.35 + (Math.random() - 0.5) * 100),
      generator3: Math.round(consumption * 0.25 + (Math.random() - 0.5) * 100),
    });
  }

  return data;
};

// Generate mock tankers
export const generateMockTankers = (): TankerData[] => {
  const sites = ['site-a', 'site-b', 'site-c', 'site-d'];
  const drivers = ['driver-1', 'driver-2', 'driver-3', 'driver-4', 'driver-5', 'driver-6'];
  const addresses = [
    'Victoria Island, Lagos',
    'Ikoyi, Lagos',
    'Surulere, Lagos',
    'Ikeja, Lagos',
    'Lekki, Lagos',
    'Ajah, Lagos',
  ];

  const tankers: TankerData[] = [];
  const registrations = ['LND-401-AB', 'LND-402-CD', 'LND-403-EF', 'LND-404-GH', 'LND-405-IJ', 'LND-406-KL'];

  for (let i = 0; i < 6; i++) {
    const capacity = 33000;
    const currentLoad = Math.floor(Math.random() * 100);

    tankers.push({
      id: `tanker-${i + 1}`,
      registrationNumber: registrations[i],
      fleetNumber: `T-${String(i + 1).padStart(2, '0')}`,
      capacity,
      fuelType: Math.random() > 0.5 ? 'petrol' : 'diesel',
      currentLoad,
      currentVolume: (currentLoad / 100) * capacity,
      status: ['en-route', 'idle', 'loading', 'delivering', 'maintenance'][Math.floor(Math.random() * 5)] as any,
      location: {
        lat: 6.4474 + (Math.random() - 0.5) * 0.2,
        lng: 3.3903 + (Math.random() - 0.5) * 0.2,
        address: addresses[i % addresses.length],
      },
      assignedDriverId: drivers[i],
      assignedSiteId: sites[i % sites.length],
      lastActivity: new Date(Date.now() - Math.random() * 2 * 60 * 60 * 1000).toISOString(),
    });
  }

  return tankers;
};

// Generate mock drivers
export const generateMockDrivers = (): DriverData[] => {
  const drivers: DriverData[] = [];
  const driverNames = [
    'Chinedu Okafor',
    'Fatima Hassan',
    'Tunde Adeyemi',
    'Zainab Ibrahim',
    'Emeka Nwankwo',
    'Aisha Abdullahi',
  ];
  const sites = ['site-a', 'site-b', 'site-c', 'site-d'];

  for (let i = 0; i < 6; i++) {
    const name = driverNames[i];
    const initials = name
      .split(' ')
      .map(n => n[0])
      .join('');

    drivers.push({
      id: `driver-${i + 1}`,
      name,
      phone: `+234-${String(800 + Math.floor(Math.random() * 10)).slice(-3)}-${Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, '0')}`,
      email: `${name.toLowerCase().replace(' ', '.')}@fuelcompany.com`,
      avatarInitials: initials,
      licenseNumber: `LD-${String(i + 1).padStart(5, '0')}`,
      licenseExpiry: new Date(Date.now() + Math.random() * 3 * 365 * 24 * 60 * 60 * 1000).toISOString(),
      assignedTankerId: `tanker-${i + 1}`,
      assignedSiteId: sites[i % sites.length],
      status: ['on-duty', 'off-duty', 'on-leave'][Math.floor(Math.random() * 3)] as any,
      totalTrips: Math.floor(Math.random() * 100) + 50,
      totalVolume: Math.floor(Math.random() * 500000) + 100000,
      trips: [
        {
          id: `trip-${i}-1`,
          date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          fromSite: 'site-a',
          toSite: 'site-b',
          volumeDelivered: Math.floor(Math.random() * 20000) + 5000,
          fuelType: Math.random() > 0.5 ? 'petrol' : 'diesel',
          status: 'completed',
        },
        {
          id: `trip-${i}-2`,
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          fromSite: 'site-b',
          toSite: 'site-c',
          volumeDelivered: Math.floor(Math.random() * 20000) + 5000,
          fuelType: Math.random() > 0.5 ? 'petrol' : 'diesel',
          status: 'completed',
        },
        {
          id: `trip-${i}-3`,
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          fromSite: 'site-c',
          toSite: 'site-d',
          volumeDelivered: Math.floor(Math.random() * 20000) + 5000,
          fuelType: Math.random() > 0.5 ? 'petrol' : 'diesel',
          status: 'completed',
        },
      ],
    });
  }

  return drivers;
};

// Generate fuel level history
export const generateFuelLevelHistory = (tankId: string, capacity: number, hours: number = 24): FuelLevelHistory[] => {
  const data: FuelLevelHistory[] = [];
  let currentLevel = 70 + Math.random() * 25; // Start at 70-95%

  for (let i = hours; i >= 0; i--) {
    const timestamp = new Date(Date.now() - i * 60 * 60 * 1000);

    // Simulate consumption decline
    const consumptionRate = 1 + Math.random() * 2; // 1-3% per hour
    currentLevel -= consumptionRate;

    // Add some randomness
    currentLevel += (Math.random() - 0.5) * 0.5;

    // Simulate refill events
    if (Math.random() > 0.98 || currentLevel < 15) {
      const refillAmount = 50 + Math.random() * 30;
      currentLevel = Math.min(95, currentLevel + refillAmount);
    }

    // Ensure level doesn't go below 5%
    currentLevel = Math.max(5, currentLevel);

    const volume = (currentLevel / 100) * capacity;

    data.push({
      timestamp: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      level: Math.round(currentLevel * 10) / 10,
      volume: Math.round(volume),
      capacity,
    });
  }

  return data;
};

// Real-time data updater
export class RealTimeDataManager {
  private sites: SiteData[] = [];
  private alerts: AlertData[] = [];
  private updateInterval: NodeJS.Timeout | null = null;
  private callbacks: Array<(sites: SiteData[], alerts: AlertData[]) => void> = [];

  constructor() {
    this.sites = generateMockSites();
    this.alerts = generateAlertsForSites(this.sites);
  }

  subscribe(callback: (sites: SiteData[], alerts: AlertData[]) => void) {
    this.callbacks.push(callback);

    // Send initial data
    callback(this.sites, this.alerts);

    return () => {
      this.callbacks = this.callbacks.filter(cb => cb !== callback);
    };
  }

  startRealTimeUpdates(intervalMs: number = 5000) {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    this.updateInterval = setInterval(() => {
      this.updateSiteData();
      this.notifyCallbacks();
    }, intervalMs);
  }

  stopRealTimeUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  private updateSiteData() {
    // Update fuel levels (simulate consumption)
    this.sites.forEach(site => {
      site.tanks.forEach(tank => {
        // Simulate gradual consumption
        if (Math.random() > 0.7) { // 30% chance of level change
          const consumptionRate = 0.1 + Math.random() * 0.3; // 0.1-0.4% per update
          tank.currentLevel = Math.max(0, tank.currentLevel - consumptionRate);
          tank.currentVolume = (tank.currentLevel / 100) * tank.capacity;
        }

        // Simulate refill events
        if (tank.currentLevel < 15 && Math.random() > 0.95) {
          const refillAmount = 50 + Math.random() * 30;
          tank.currentLevel = Math.min(95, tank.currentLevel + refillAmount);
          tank.currentVolume = (tank.currentLevel / 100) * tank.capacity;

          // Add refill alert
          this.alerts.unshift({
            id: `alert-${Date.now()}-${Math.random()}`,
            type: 'refill',
            severity: 'low',
            title: 'Fuel Delivery Completed',
            message: `${site.name} - ${tank.name} refilled to ${tank.currentLevel.toFixed(1)}%`,
            timestamp: new Date().toISOString(),
            siteId: site.id,
            tankId: tank.id,
            acknowledged: false,
            resolved: false,
          });
        }

        // Generate new low fuel alerts
        if (tank.currentLevel < 25 && !this.alerts.some(alert =>
          alert.type === 'low_fuel' &&
          alert.tankId === tank.id &&
          !alert.resolved
        )) {
          this.alerts.unshift({
            id: `alert-${Date.now()}-${Math.random()}`,
            type: 'low_fuel',
            severity: tank.currentLevel < 10 ? 'critical' : 'medium',
            title: 'Low Fuel Level',
            message: `${site.name} - ${tank.name} is at ${tank.currentLevel.toFixed(1)}%`,
            timestamp: new Date().toISOString(),
            siteId: site.id,
            tankId: tank.id,
            acknowledged: false,
            resolved: false,
          });
        }
      });

      // Update generator runtime
      site.generators.forEach(generator => {
        if (generator.status === 'running') {
          generator.runtime += 0.001; // Add a small amount of runtime
        }
      });
    });

    // Clean up old alerts (keep last 50)
    this.alerts = this.alerts.slice(0, 50);
  }

  private notifyCallbacks() {
    this.callbacks.forEach(callback => {
      callback([...this.sites], [...this.alerts]);
    });
  }

  getSites() {
    return [...this.sites];
  }

  getAlerts() {
    return [...this.alerts];
  }
}

// Singleton instance
export const realTimeDataManager = new RealTimeDataManager();